(() => {
  const ROOT_SELECTOR = '[data-rsg-fuel-finder-runtime-src]';
  const RUNTIME_SELECTOR = 'script[data-rsg-fuel-finder-runtime]';
  const TOOL_HASH = '#fuel-finder-tool';
  const TARGET_THEME_ID = 198274056527;
  const VALID_PACK_SOURCES = new Set(['fuel_pack', 'guide_fuel_packs']);
  const globalState = window.__RSGFuelFinderRuntimeLoader || {
    promise: null,
    failedScript: null,
    observer: null,
    bound: false,
    fallbackScheduled: false,
  };

  if (typeof globalState.fallbackScheduled !== 'boolean') globalState.fallbackScheduled = false;

  window.__RSGFuelFinderRuntimeLoader = globalState;

  const exactCandidatePreview = () => (
    Number(window.Shopify?.theme?.id) === TARGET_THEME_ID
    && String(window.Shopify?.theme?.role || '').toLowerCase() === 'unpublished'
    && window.Shopify?.previewMode === true
  );

  const sanitiseDebugQuery = () => {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('rsgDebug') || exactCandidatePreview()) return;
      url.searchParams.delete('rsgDebug');
      window.history?.replaceState?.(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`,
      );
    } catch (_error) {}
  };

  const hidePrivateDebugApi = () => {
    if (exactCandidatePreview()) return;
    try { delete window.RSGFuelFinderDebug; } catch (_error) {}
  };

  const roots = () => Array.from(document.querySelectorAll(ROOT_SELECTOR));

  const setLoading = (root, reason) => {
    root.dataset.rsgFuelFinderLoaderState = 'loading';
    root.dataset.rsgFuelFinderLoaderReason = reason;
    const message = root.querySelector('[data-rsg-fuel-finder-loading]');
    if (message) message.hidden = false;
  };

  const setReady = (root) => {
    root.dataset.rsgFuelFinderLoaderState = 'ready';
    delete root.dataset.rsgFuelFinderLoaderReason;
    const message = root.querySelector('[data-rsg-fuel-finder-loading]');
    if (message) message.hidden = true;
  };

  const setError = (root) => {
    root.dataset.rsgFuelFinderLoaderState = 'error';
    root.innerHTML = [
      '<div class="rsg-ff-errors" role="alert">',
      '<p>We couldn\'t load the Fuel Finder. Check your connection and try again.</p>',
      '<button type="button" class="rsg-ff-secondary" data-rsg-fuel-finder-retry>Try again</button>',
      '</div>',
    ].join('');
  };

  const mountRoot = (root) => {
    if (!root || !window.RSGFuelFinder || typeof window.RSGFuelFinder.mount !== 'function') return false;
    try {
      const mounted = window.RSGFuelFinder.mount({ rootId: root.id, rootElement: root });
      const ready = Boolean(mounted || root.hasAttribute('data-rsg-fuel-finder-build'));
      if (ready) setReady(root);
      return ready;
    } catch (_error) {
      return false;
    }
  };

  const mountAll = () => {
    const candidateRoots = roots();
    const readyRoots = candidateRoots.filter((root) => {
      const ready = mountRoot(root);
      if (!ready) setError(root);
      return ready;
    });
    if (readyRoots.length > 0) {
      document.dispatchEvent(new CustomEvent('rsg:fuel-finder-runtime-ready', {
        detail: { rootCount: readyRoots.length },
      }));
    }
    return readyRoots;
  };

  const completeMount = () => {
    hidePrivateDebugApi();
    const readyRoots = mountAll();
    if (readyRoots.length === 0) throw new Error('Fuel Finder runtime did not mount');
    globalState.failedScript = null;
    return true;
  };

  const runtimeSource = () => roots()
    .map((root) => root.dataset.rsgFuelFinderRuntimeSrc)
    .find(Boolean);

  const ensureRuntime = (reason = 'manual') => {
    const currentRoots = roots();
    currentRoots.forEach((root) => setLoading(root, reason));

    if (window.RSGFuelFinder && typeof window.RSGFuelFinder.mount === 'function') {
      return Promise.resolve()
        .then(completeMount)
        .catch(() => {
          roots().forEach(setError);
          return false;
        });
    }

    if (globalState.promise) return globalState.promise;

    const source = runtimeSource();
    if (!source) {
      currentRoots.forEach(setError);
      return Promise.resolve(false);
    }

    if (globalState.failedScript?.isConnected) globalState.failedScript.remove();
    globalState.failedScript = null;

    globalState.promise = new Promise((resolve, reject) => {
      let script = document.querySelector(RUNTIME_SELECTOR);

      const loaded = () => {
        script.dataset.rsgFuelFinderRuntimeLoaded = 'true';
        resolve();
      };
      const failed = () => reject(new Error('Fuel Finder runtime request failed'));

      if (script?.dataset.rsgFuelFinderRuntimeLoaded === 'true') {
        loaded();
        return;
      }

      if (!script) {
        script = document.createElement('script');
        script.src = source;
        script.async = true;
        script.dataset.rsgFuelFinderRuntime = 'true';
      }

      script.addEventListener('load', loaded, { once: true });
      script.addEventListener('error', failed, { once: true });
      if (!script.isConnected) document.head.appendChild(script);
    })
      .then(() => {
        if (!window.RSGFuelFinder || typeof window.RSGFuelFinder.mount !== 'function') {
          throw new Error('Fuel Finder runtime did not initialise');
        }
        return completeMount();
      })
      .catch(() => {
        const failedScript = document.querySelector(RUNTIME_SELECTOR);
        if (failedScript) globalState.failedScript = failedScript;
        globalState.promise = null;
        roots().forEach(setError);
        return false;
      });

    return globalState.promise;
  };

  const samePageToolLink = (target) => {
    const link = target?.closest?.('a[href]');
    if (!link) return false;
    try {
      const url = new URL(link.href, window.location.href);
      return url.origin === window.location.origin
        && url.pathname === window.location.pathname
        && url.hash === TOOL_HASH;
    } catch (_error) {
      return false;
    }
  };

  const validPackHandoff = () => {
    try {
      return VALID_PACK_SOURCES.has(new URLSearchParams(window.location.search).get('rsg_pack_source'));
    } catch (_error) {
      return false;
    }
  };

  const schedulePostLoadFallback = () => {
    if ('IntersectionObserver' in window) return;
    if (globalState.fallbackScheduled) return;
    globalState.fallbackScheduled = true;
    const queue = () => {
      const run = () => { void ensureRuntime('post-load-idle'); };
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(run, { timeout: 2500 });
      } else {
        window.setTimeout(run, 0);
      }
    };
    if (document.readyState === 'complete') queue();
    else window.addEventListener('load', queue, { once: true });
  };

  const observeRoots = () => {
    if (!('IntersectionObserver' in window)) return;
    if (!globalState.observer) {
      globalState.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          globalState.observer.unobserve(entry.target);
          void ensureRuntime('tool-visible');
        });
      }, { rootMargin: '0px', threshold: 0.01 });
    }
    roots().forEach((root) => globalState.observer.observe(root));
  };

  const initialise = () => {
    observeRoots();

    if (window.location.hash === TOOL_HASH) void ensureRuntime('direct-anchor');
    if (validPackHandoff()) void ensureRuntime('fuel-pack-handoff');
    if (window.Shopify?.designMode) void ensureRuntime('theme-editor');

    schedulePostLoadFallback();

    if (globalState.bound) return;
    globalState.bound = true;

    const activateFromIntent = (event) => {
      if (event.target?.closest?.('[data-rsg-fuel-finder-retry]')) {
        void ensureRuntime('retry');
        return;
      }
      if (samePageToolLink(event.target)) void ensureRuntime('tool-link');
    };

    document.addEventListener('focusin', activateFromIntent, true);
    document.addEventListener('pointerdown', activateFromIntent, true);
    document.addEventListener('click', activateFromIntent, true);
    window.addEventListener('pageshow', () => {
      observeRoots();
      if (window.location.hash === TOOL_HASH || validPackHandoff()) void ensureRuntime('page-show');
    });
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target?.matches?.(ROOT_SELECTOR) || event.target?.querySelector?.(ROOT_SELECTOR)) {
        observeRoots();
        void ensureRuntime('theme-editor-section');
      }
    });
  };

  window.RSGFuelFinderLoader = Object.freeze({ ensureRuntime, mountAll });
  sanitiseDebugQuery();
  initialise();
})();
